import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle } from 'lucide-react';
import { getAnalyticsSessionId, trackEvent } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const MD_COUNTIES = [
  'Allegany', 'Anne Arundel', 'Baltimore City', 'Baltimore County', 'Calvert', 'Caroline',
  'Carroll', 'Cecil', 'Charles', 'Dorchester', 'Frederick', 'Garrett', 'Harford', 'Howard',
  'Kent', 'Montgomery', "Prince George's", "Queen Anne's", "Saint Mary's", 'Somerset',
  'Talbot', 'Washington', 'Wicomico', 'Worcester'
];

const ElectionJudgePage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', county: '',
    address_search: '', street: '', city: '', state: 'Maryland', country: 'United States', zip_code: '', optin: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.county) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await trackEvent('form_submit', '/be-an-election-judge', null, { metadata: { form_type: 'election-judge-interest' } });
      await axios.post(`${API_URL}/api/contact/submit`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        terms_accepted: true,
        source: 'election-judge-interest',
        zip_code: form.zip_code,
        message: `County: ${form.county}\nAddress Search: ${form.address_search}\nStreet Address: ${form.street}\nCity: ${form.city}\nState: ${form.state}\nCountry: ${form.country}\nPostal Code: ${form.zip_code}`,
        session_id: getAnalyticsSessionId(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="election-judge-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* Hero */}
      <div className="relative pt-[50px]">
        <div className="absolute inset-0" style={{
          backgroundColor: '#9E1B32',
          backgroundImage: `url('https://securethevotemd.com/images/2025/03/2025-Feb-25-Secure-the-Vorte-66-1024x683.jpg')`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }} />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.75)' }} />
        <div className="relative z-10 py-20 sm:py-28">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Be an Election Judge
            </motion.h1>
            <motion.div
              className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.3 }}
            />
          </div>
        </div>
      </div>

      <main className="py-12 sm:py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* Left — Page Content */}
            <motion.div
              className="lg:col-span-3 space-y-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-bold text-[#9B1E37]">Become an Election Judge in 2026</h2>
              <p className="text-slate-600 leading-relaxed">SecureTheVoteMD is seeking community members to serve as election judges and help ensure fair, accessible, and secure elections. No prior experience is required — comprehensive training is provided.</p>

              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Election judges are responsible for:</h3>
                <ul className="space-y-1.5 text-slate-600">
                  <li>Setting up and closing polling places</li>
                  <li>Checking in and assisting voters</li>
                  <li>Overseeing election procedures</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Minimum requirements:</h3>
                <ul className="space-y-1.5 text-slate-600">
                  <li>Registered voter in Maryland</li>
                  <li>Able to read, write, and speak English</li>
                  <li>Physically and mentally able to work a 15-hour day</li>
                  <li>Willing to work outside your home precinct</li>
                  <li>Able to sit or stand for long periods</li>
                </ul>
              </div>

              <div>
                <p className="text-slate-500 text-sm italic">Not eligible: Candidates, elected officials, political party central committee members, or campaign officers (chair, manager, or treasurer).</p>
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Preferred but not required:</h3>
                <ul className="space-y-1.5 text-slate-600">
                  <li>Computer familiarity</li>
                  <li>Bilingual skills (highly encouraged)</li>
                  <li>Ability to lift 10–25 lbs (for certain roles)</li>
                </ul>
              </div>

              <p className="text-slate-600 leading-relaxed">If you are ready to serve your community and safeguard the democratic process, please complete the <strong>Election Judge Interest Form</strong>.</p>
            </motion.div>

            {/* Right — Form */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <div className="sticky top-24 bg-slate-50 rounded-xl p-6 border" data-testid="judge-form-card">
                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h3>
                    <p className="text-slate-600 text-sm">We've received your interest form and will be in touch with more information about becoming an election judge.</p>
                  </div>
                ) : (
                  <>
                    <h2 className="text-lg font-bold text-[#9B1E37] mb-1">Election Judge Interest</h2>
                    <p className="text-sm text-slate-500 mb-5">Fill out the form below, and we'll be in touch with more information.</p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-800">First Name <span className="text-red-500">*</span></Label>
                        <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First Name" className="h-11 border-slate-300" data-testid="judge-first-name" />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-800">Last Name <span className="text-red-500">*</span></Label>
                        <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last Name" className="h-11 border-slate-300" data-testid="judge-last-name" />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-800">Email <span className="text-red-500">*</span></Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="h-11 border-slate-300" data-testid="judge-email" />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-800">Phone <span className="text-red-500">*</span></Label>
                        <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="h-11 border-slate-300" data-testid="judge-phone" />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-800">County <span className="text-red-500">*</span></Label>
                        <select
                          value={form.county}
                          onChange={(e) => setForm({ ...form, county: e.target.value })}
                          className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm bg-white"
                          data-testid="judge-county"
                        >
                          <option value="">County of Residence</option>
                          {MD_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-800">Address</Label>
                        <Input value={form.address_search} onChange={(e) => setForm({ ...form, address_search: e.target.value })} placeholder="Search address" className="h-11 border-slate-300" />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-slate-800">Street Address</Label>
                        <Input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} placeholder="Address" className="h-11 border-slate-300" />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-sm font-semibold text-slate-800">City</Label>
                          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="h-11 border-slate-300" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-sm font-semibold text-slate-800">State</Label>
                          <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" className="h-11 border-slate-300" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-sm font-semibold text-slate-800">Country</Label>
                          <select
                            value={form.country}
                            onChange={(e) => setForm({ ...form, country: e.target.value })}
                            className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm bg-white"
                          >
                            <option value="United States">United States</option>
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-sm font-semibold text-slate-800">Postal Code</Label>
                          <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="Postal Code" maxLength={10} className="h-11 border-slate-300" />
                        </div>
                      </div>

                      <div className="flex items-start gap-3 pt-1">
                        <input type="checkbox" checked={form.optin} onChange={(e) => setForm({ ...form, optin: e.target.checked })} className="mt-1 h-4 w-4 accent-[#9B1E37]" />
                        <span className="text-xs text-slate-500 leading-relaxed">I agree to terms &amp; conditions provided by SecureTheVoteMD. By providing my phone number, I agree to receive text messages from STV.</span>
                      </div>

                      {error && <p className="text-red-500 text-sm">{error}</p>}

                      <Button type="submit" disabled={submitting} className="w-full h-11 font-bold tracking-wide" style={{ backgroundColor: '#F6BF58', color: '#1a1a1a' }} data-testid="judge-submit">
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : 'SUBMIT'}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default ElectionJudgePage;

